import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Download, Loader2, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { Event, EventPickupRequest, PickupRequestStatus } from '../../types';
import { cn } from '../../lib/utils';
import { useApiJson } from '../../hooks/useApi';
import {
  downloadPickupRequestsExport,
  eventPickupRequestsPath,
  updatePickupRequestStatus,
  type PickupRequestsResponse,
} from '../../lib/events-api';
import {
  filterPickupRequests,
  isPickupRequestStatus,
  parsePickupSort,
  sortPickupRequests,
} from '../../lib/pickup-list';

const STATUSES: PickupRequestStatus[] = ['new', 'scheduled', 'picked_up', 'cancelled'];

const STATUS_LABELS: Record<PickupRequestStatus, string> = {
  new: 'New',
  scheduled: 'Scheduled',
  picked_up: 'Picked up',
  cancelled: 'Cancelled',
};

const STATUS_STYLES: Record<PickupRequestStatus, string> = {
  new: 'bg-[var(--color-warning-subtle)] text-[var(--color-text-primary)]',
  scheduled: 'bg-[var(--color-info-subtle)] text-[var(--color-text-secondary)]',
  picked_up: 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]',
  cancelled: 'bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]',
};

function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 min-h-11 px-3 rounded-[var(--radius-full)] text-sm font-medium transition-colors',
        selected
          ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]',
      )}
    >
      {children}
    </button>
  );
}

function Count({ n }: { n: number }) {
  return <span className="tabular-nums opacity-70">{n}</span>;
}

export function EventPickupRequestsPanel({
  event,
  events,
  onChanged,
  onSelectEvent,
}: {
  event: Event;
  events: Event[];
  onChanged: () => Promise<void>;
  onSelectEvent: (eventId: string) => void;
}) {
  const { data, loading, error, mutate } = useApiJson<PickupRequestsResponse>(eventPickupRequestsPath(event.id));
  const [searchParams, setSearchParams] = useSearchParams();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);

  const query = searchParams.get('q') ?? '';
  const statusFilter = isPickupRequestStatus(searchParams.get('status')) ? searchParams.get('status')! : '';
  const sort = parsePickupSort(searchParams.get('sort')) ?? 'newest';

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const requests = data?.requests ?? [];
  const searched = useMemo(() => filterPickupRequests(requests, { q: query }), [requests, query]);
  const statusCounts = useMemo(() => {
    const counts: Record<PickupRequestStatus, number> = { new: 0, scheduled: 0, picked_up: 0, cancelled: 0 };
    for (const row of searched) counts[row.status] += 1;
    return counts;
  }, [searched]);
  const filtered = useMemo(
    () => sortPickupRequests(filterPickupRequests(searched, { status: statusFilter || null }), sort),
    [searched, statusFilter, sort],
  );

  const refresh = async () => {
    await mutate();
    await onChanged();
  };

  const handleStatus = async (row: EventPickupRequest, status: PickupRequestStatus) => {
    setBusyId(row.id);
    setActionError(null);
    try {
      await updatePickupRequestStatus(event.id, row.id, status);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update status');
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = async (fmt: 'csv' | 'xlsx') => {
    setExporting(fmt);
    setActionError(null);
    try {
      await downloadPickupRequestsExport(event.id, fmt, event.slug || event.id, {
        q: query,
        status: statusFilter || undefined,
        sort,
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const clearFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('q');
        next.delete('status');
        next.delete('sort');
        return next;
      },
      { replace: true },
    );
  };

  const filtersActive = Boolean(query || statusFilter || sort === 'oldest');
  const countLabel =
    filtered.length === requests.length
      ? `${requests.length} pickup request${requests.length === 1 ? '' : 's'}`
      : `${filtered.length} of ${requests.length} shown`;

  const selectClass =
    'bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2 px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] min-h-11';

  const statusControl = (row: EventPickupRequest) => (
    <select
      value={row.status}
      disabled={busyId === row.id}
      onChange={(ev) => void handleStatus(row, ev.target.value as PickupRequestStatus)}
      className={cn(
        'text-xs font-medium rounded-[var(--radius-sm)] py-1.5 px-2 border-0 outline-none focus:ring-2 focus:ring-[var(--color-accent-subtle)] min-h-9',
        STATUS_STYLES[row.status],
      )}
      aria-label={`Status for ${row.name}`}
    >
      {STATUSES.map((status) => (
        <option key={status} value={status}>
          {STATUS_LABELS[status]}
        </option>
      ))}
    </select>
  );

  return (
    <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)]">
      <div className="p-4 sm:p-5 border-b border-[var(--color-border)] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]">Pickup requests</p>
            {events.length > 1 ? (
              <label className="block max-w-md mt-1">
                <span className="sr-only">Event</span>
                <select
                  className={cn(selectClass, 'w-full font-semibold')}
                  value={event.id}
                  onChange={(ev) => onSelectEvent(ev.target.value)}
                  aria-label="Event"
                >
                  {events.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <h3 className="text-base font-semibold text-[var(--color-text-primary)] mt-1">{event.title}</h3>
            )}
            <p className="text-xs text-[var(--color-text-secondary)] mt-1.5">
              {format(new Date(event.date), 'PPP p')}
              {requests.length > 0 && ` · ${countLabel}`}
            </p>
          </div>
          <div className="flex gap-2">
            {(['csv', 'xlsx'] as const).map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => void handleExport(fmt)}
                disabled={exporting !== null || filtered.length === 0}
                title="Exports the pickup requests currently shown"
                className="inline-flex items-center gap-1.5 min-h-11 px-3 py-2 rounded-[var(--radius-md)] text-sm font-medium border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] disabled:opacity-50"
              >
                {exporting === fmt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by pickup status">
          <FilterChip selected={statusFilter === ''} onClick={() => setParam('status', '')}>
            All <Count n={searched.length} />
          </FilterChip>
          {STATUSES.map((status) => (
            <FilterChip
              key={status}
              selected={statusFilter === status}
              onClick={() => setParam('status', statusFilter === status ? '' : status)}
            >
              {STATUS_LABELS[status]} <Count n={statusCounts[status]} />
            </FilterChip>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-48">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
            <input
              type="search"
              placeholder="Name, phone, or address"
              value={query}
              onChange={(ev) => setParam('q', ev.target.value)}
              aria-label="Search pickup requests"
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2 pl-9 pr-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] min-h-11"
            />
          </div>
          <select
            className={selectClass}
            value={sort}
            onChange={(ev) => setParam('sort', ev.target.value === 'oldest' ? 'oldest' : '')}
            aria-label="Sort pickup requests"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="min-h-11 px-3 text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
            >
              Clear
            </button>
          )}
        </div>

        {actionError && (
          <p role="alert" className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2">
            {actionError}
          </p>
        )}
      </div>

      {loading ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-tertiary)]" />
        </div>
      ) : error ? (
        <p className="p-6 text-sm text-[var(--color-error-text)]">{error.message}</p>
      ) : filtered.length === 0 ? (
        <p className="p-10 text-sm text-center text-[var(--color-text-tertiary)]">
          {requests.length === 0
            ? 'No pickup requests yet. They appear here when someone uses Request pickup.'
            : 'No pickup requests match these filters.'}
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)]">
          {filtered.map((row) => (
            <li key={row.id} className="p-4 sm:p-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">{row.name}</p>
                <p className="text-sm font-mono text-[var(--color-text-secondary)]">{row.phone}</p>
                <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-line">{row.address}</p>
                <p className="text-xs text-[var(--color-text-tertiary)]">{format(new Date(row.createdAt), 'PPP p')}</p>
              </div>
              {statusControl(row)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
